import {
  IAnswerMap,
  IAnswerValue,
  IDraftAnswer,
  IFlowScreen,
  IFlowShape,
  IFlowStep,
  IQuestion,
  ITaskCreationConfig,
  ITaskCreationMode,
} from "../types";
import {
  AUTH_SCREEN_KEYS,
  EDIT_HIDDEN_QUESTION_KEYS,
  STEP_KEYS,
  SUBCATEGORY_SCREEN_KEY,
} from "../config/constants";
import { isVisible } from "./conditions";
import { isSuggestedAddress } from "./suggestedAddressValues";

const byPosition = <T extends { position: number }>(a: T, b: T) =>
  a.position - b.position;

interface IBuildFlowShapeOptions {
  /** A logged-in user skips contact details, so 2 steps rather than 3. */
  isLoggedIn: boolean;
  /**
   * Editing a published request keeps its subcategory and its audience, so
   * neither is asked again. Defaults to the create flow.
   */
  mode?: ITaskCreationMode;
}

/**
 * The screens the user will actually walk, and the step rows to show alongside
 * them.
 *
 * Everything that needs to agree about the flow's length reads this one result:
 * the progress bar, the "step 1 of N" header, the summary rows, and
 * `findFirstIncompleteScreen`. That is what keeps them from drifting apart.
 *
 * Screens whose every question is hidden are dropped, so a config that omits a
 * screen — or hides all of it behind `visible_if` — degrades to a skipped step
 * rather than an empty page.
 */
export const buildFlowShape = (
  config: ITaskCreationConfig,
  answers: IAnswerMap,
  { isLoggedIn, mode = "create" }: IBuildFlowShapeOptions
): IFlowShape => {
  // Always first, for every visitor. A SEM entry is redirected *past* it
  // (`ServiceRequestCreationRoot`) and its draft marks it complete, which is a
  // different thing from the screen not existing: dropping it made the flow's
  // own shape depend on a query param that only rides the entry URL, so one
  // hop later the same visitor had a different flow — different step numbers,
  // and a Back that skipped a screen (RT-4321).
  //
  // Except when editing: the request already has a subcategory, and changing
  // it would reset every answer server-side.
  const screens: IFlowScreen[] =
    mode === "edit"
      ? []
      : [
          {
            key: SUBCATEGORY_SCREEN_KEY,
            stepKey: STEP_KEYS.SERVICE_DETAILS,
            source: "static",
            questions: [],
          },
        ];

  [...config.steps].sort(byPosition).forEach((step) => {
    [...step.screens].sort(byPosition).forEach((screen) => {
      const questions: IQuestion[] = [...screen.questions]
        .sort(byPosition)
        .filter(
          (question) =>
            isVisible(question, answers) &&
            !(
              mode === "edit" && EDIT_HIDDEN_QUESTION_KEYS.includes(question.key)
            )
        );

      if (questions.length === 0) return;

      screens.push({
        key: screen.key,
        stepKey: step.key,
        source: "config",
        questions,
      });
    });
  });

  // A visitor says who they are before the request can be published. Only the
  // email screen is listed — nothing here knows which branch follows.
  if (!isLoggedIn) {
    screens.push({
      key: AUTH_SCREEN_KEYS.EMAIL,
      stepKey: STEP_KEYS.CONTACT_DETAILS,
      source: "static",
      questions: [],
    });
  }

  const configSteps: IFlowStep[] = [...config.steps]
    .sort(byPosition)
    .map((step) => ({
      key: step.key,
      position: step.position,
      screenKeys: screens
        .filter((screen) => screen.stepKey === step.key)
        .map((screen) => screen.key),
    }));

  const steps = isLoggedIn
    ? configSteps
    : [
        ...configSteps,
        {
          key: STEP_KEYS.CONTACT_DETAILS,
          position: configSteps.length + 1,
          screenKeys: [AUTH_SCREEN_KEYS.EMAIL],
        },
      ];

  return { screens, steps };
};

/**
 * Whether a question has been answered. `0` and `false` are answers; an empty
 * string and an empty array are not.
 */
const isAnswered = (answers: IAnswerMap, key: string): boolean => {
  const value = answers[key];
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

interface IIncompleteScreenOptions {
  /**
   * Whether the draft already carries an `s_category_id`. The picker is a
   * static screen with no questions, so nothing in `answers` can say whether it
   * is done — this is its completeness signal.
   */
  isSubcategoryChosen?: boolean;
}

/**
 * Whether a screen has nothing left to fill in.
 *
 * Exported because completeness is not only the router's business: the progress
 * bar counts complete screens, and the summary lists them. One rule, one place —
 * a second copy would drift from the router's idea of "earned".
 */
export const isScreenComplete = (
  screen: IFlowScreen,
  answers: IAnswerMap,
  { isSubcategoryChosen = false }: IIncompleteScreenOptions = {}
): boolean => {
  if (screen.key === SUBCATEGORY_SCREEN_KEY) return isSubcategoryChosen;
  // Never complete: it is only in the shape while the visitor is logged out.
  if (screen.key === AUTH_SCREEN_KEYS.EMAIL) return false;

  return screen.questions.every(
    (question) => !question.required || isAnswered(answers, question.key)
  );
};

/**
 * Required-question counts across every screen, for the progress bar: it
 * advances per question answered, not per screen completed. Optional
 * questions are ignored, matching `isScreenComplete`'s own rule. The
 * subcategory and auth screens have no questions, so each counts as one
 * required unit — the same special case as above.
 */
export const countQuestionProgress = (
  screens: IFlowScreen[],
  answers: IAnswerMap,
  { isSubcategoryChosen = false }: IIncompleteScreenOptions = {}
): { answered: number; total: number } =>
  screens.reduce(
    (counts, screen) => {
      if (screen.key === SUBCATEGORY_SCREEN_KEY) {
        return {
          answered: counts.answered + (isSubcategoryChosen ? 1 : 0),
          total: counts.total + 1,
        };
      }

      // One unit, never met, or the bar reads 100% before they log in.
      if (screen.key === AUTH_SCREEN_KEYS.EMAIL)
        return { answered: counts.answered, total: counts.total + 1 };

      const required = screen.questions.filter((question) => question.required);

      return {
        answered:
          counts.answered +
          required.filter((question) => isAnswered(answers, question.key))
            .length,
        total: counts.total + required.length,
      };
    },
    { answered: 0, total: 0 }
  );

/**
 * Required-question counts across every screen, for the progress bar: it
 * reflects the visitor's *position* in the flow, not what they have actually
 * filled in — every screen up to and including the current one counts in
 * full the moment it is reached, unanswered questions included, and going
 * Back drops it right back down. `total` still moves with the flow shape
 * itself (an inline question appearing or dropping out changes both sides).
 *
 * Every screen counts at least 1, even one whose questions are all optional
 * (painter's `area` holds only two optional fields) — otherwise crossing it
 * moved the visitor's position without moving the bar at all.
 *
 * `currentScreenKey` absent, or not found in `screens` (config still
 * loading), counts nothing as reached — same as not having arrived anywhere
 * yet.
 */
export const countScreenPositionProgress = (
  screens: IFlowScreen[],
  currentScreenKey: string | undefined
): { answered: number; total: number } => {
  const currentIndex = screens.findIndex(
    (screen) => screen.key === currentScreenKey
  );

  return screens.reduce(
    (counts, screen, index) => {
      // The two synthetic screens carry no config questions, so they are
      // worth one unit each by hand — without it the auth step is invisible
      // on the bar and it reads 100% on the screen before it.
      const count =
        screen.key === SUBCATEGORY_SCREEN_KEY ||
        screen.key === AUTH_SCREEN_KEYS.EMAIL
          ? 1
          : Math.max(
              screen.questions.filter((question) => question.required).length,
              1
            );

      return {
        answered:
          counts.answered +
          (currentIndex !== -1 && index <= currentIndex ? count : 0),
        total: counts.total + count,
      };
    },
    { answered: 0, total: 0 }
  );
};

/**
 * The furthest screen the user has earned. Everything before it is editable,
 * everything after it is out of reach — which is what makes a screen key in the
 * URL safe to accept.
 *
 * Returns the last screen when every required question is answered, so a
 * completed flow lands on its final screen rather than off the end.
 */
export const findFirstIncompleteScreen = (
  { screens }: IFlowShape,
  answers: IAnswerMap,
  { isSubcategoryChosen = false }: IIncompleteScreenOptions = {}
): IFlowScreen | undefined => {
  const incomplete = screens.find(
    (screen) => !isScreenComplete(screen, answers, { isSubcategoryChosen })
  );

  if (incomplete) return incomplete;

  return screens[screens.length - 1];
};

/**
 * Drops choice values the current config no longer offers (a renamed or
 * removed option). `answer.question` is the live question row, keyed by id,
 * so its `options` reflect today's config even for an answer saved against an
 * older one (RT-edit-flow). Left as-is for every other question type.
 */
const dropStaleOptions = (
  value: IAnswerValue | undefined,
  question?: IQuestion
): IAnswerValue | undefined => {
  if (!question?.options) return value;
  const options = question.options;

  if (question.type === "multi_select") {
    return Array.isArray(value)
      ? ((value as (string | number)[]).filter((v) =>
          options.includes(String(v))
        ) as IAnswerValue)
      : value;
  }

  if (question.type === "single_select") {
    return typeof value === "string" && !question.options.includes(value)
      ? undefined
      : value;
  }

  return value;
};

/**
 * Turns a draft's answer list into a flat map, which is what every condition,
 * hint and validation rule reads.
 *
 * Prefers the user's own value and falls back to the assistant's, so a
 * prefilled-but-untouched question still counts as answered.
 */
export const toAnswerMap = (answers: IDraftAnswer[] = []): IAnswerMap =>
  answers.reduce<IAnswerMap>((map, answer) => {
    // The suggested address is no answer value: the address screen seeds its
    // fields from it instead (`suggestedAddressValues`).
    const aiValue = isSuggestedAddress(answer.ai_value)
      ? undefined
      : answer.ai_value;
    const value = answer.value ?? aiValue ?? undefined;
    map[answer.question_key] = dropStaleOptions(value, answer.question);
    return map;
  }, {});
